import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Typography,
    TextField,
    Button,
    CircularProgress,
    Grid,
    Avatar,
    Chip,
    Tooltip,
    InputAdornment,
    IconButton,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import LinearProgress from "@material-ui/core/LinearProgress";

import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import PersonIcon from "@material-ui/icons/Person";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import RefreshIcon from "@material-ui/icons/Refresh";
import ClearIcon from "@material-ui/icons/Close";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PhoneIcon from "@material-ui/icons/Phone";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import SendIcon from "@material-ui/icons/Send";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";

// Shared Decorpot palette - the same constants as users, roles, cronJobMonitor
// and sendNotification, so this page recolours with them instead of carrying
// its own navy.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const INK = "#3d2b21";
const GREEN = "#1e7e34";
const GREEN_DARK = "#19692c";
const GREEN_LIGHT = "#eaf6ee";
const RED = "#a93226";
const RED_LIGHT = "#fdecea";

// Two letters then seven digits, e.g. BN1234567. The server enforces the same
// pattern, so keeping it identical here means the form never lets through
// something the API will only reject after a round trip.
const LEAD_REGEX = /^[A-Z]{2}\d{7}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LEAD_HINT = "Unique Lead Number only — 2 letters then 7 digits, e.g. BN1234567";

// Says what is wrong with THIS value, not just what the rule is. "you have 5"
// is actionable; "invalid lead number" sends people back to the start.
const leadHelp = (value) => {
    if (!value) return "Unique Lead Number only · e.g. BN1234567";
    if (LEAD_REGEX.test(value)) return " ";
    const letters = (value.match(/^[A-Z]*/) || [""])[0];
    if (letters.length < 2) return `Starts with 2 letters — you have ${letters.length}`;
    const digits = value.slice(2);
    if (!/^\d*$/.test(digits)) return "After the 2 letters it is digits only";
    return `Needs 7 digits after ${letters} — you have ${digits.length}`;
};

// Strips what can never belong in a Unique Lead Number - spaces, dashes, the
// stray characters that come with a copy-paste - and caps the length. It does
// NOT reorder or drop characters by position: silently turning a bad paste
// into a different valid-looking number would be worse than rejecting it.
const sanitiseLead = (raw) => String(raw).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
    page: {
        display: "flex",
        justifyContent: "center",
        padding: "8px 4px 40px",
    },
    shell: { width: "100%", maxWidth: 860 },

    banner: {
        display: "flex", alignItems: "center", gap: 16,
        padding: "14px 22px", borderRadius: 16,
        background: HEADER_GRADIENT, color: "#fff",
        boxShadow: "0 8px 24px rgba(93,55,28,0.18)", marginBottom: 14,
        [theme.breakpoints.down("xs")]: { padding: "14px 16px", flexWrap: "wrap" },
    },
    bannerIcon: {
        display: "grid", placeItems: "center", width: 44, height: 44,
        borderRadius: 14, background: "rgba(255,255,255,0.18)", flexShrink: 0,
    },
    bannerTitle: { fontSize: 19, fontWeight: 700, lineHeight: 1.3 },
    bannerSub: { fontSize: 12.5, color: "rgba(255,255,255,0.88)", marginTop: 3 },

    card: {
        borderRadius: 18, background: "#fff",
        border: "1px solid #eceff3",
        boxShadow: "0 4px 24px rgba(93,55,28,0.08)",
        padding: "22px 24px 20px",
        [theme.breakpoints.down("xs")]: { padding: "18px 16px" },
    },

    stepLabel: {
        display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
    },
    stepNum: {
        width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
        display: "grid", placeItems: "center",
        fontSize: 12, fontWeight: 800, color: "#fff", background: ORANGE_DARK,
    },
    stepTitle: { fontSize: 14.5, fontWeight: 700, color: INK },
    stepSub: { fontSize: 12, color: "#636c7a", marginLeft: 2 },

    // The gift goes to the referrer, and which of the two numbers is which is
    // the single easiest thing to get wrong on this form - so each step says
    // what it is for, not just what to type.
    divider: {
        display: "flex", alignItems: "center", gap: 12,
        margin: "22px 0 18px",
    },
    dividerLine: { flex: 1, height: 1, background: "#eceff3" },
    dividerIcon: {
        width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center",
        background: ORANGE_LIGHT, color: ORANGE_DARK, flexShrink: 0,
    },

    // ---- lookup result ------------------------------------------------------
    lookupCard: {
        display: "flex", alignItems: "center", gap: 12, marginTop: 10,
        padding: "10px 14px", borderRadius: 14,
        border: "1px solid", minHeight: 44,
        transition: "background-color 200ms ease, border-color 200ms ease",
    },
    lookupIdle: { background: "#fbfaf9", borderColor: "#ece8e5", color: "#636c7a" },
    lookupOk: { background: GREEN_LIGHT, borderColor: "#bfe3c9" },
    lookupWarn: { background: "#fff4e2", borderColor: "#f3d8ab" },
    lookupBad: { background: RED_LIGHT, borderColor: "#f4c7c1" },
    lookupAvatar: { width: 34, height: 34, fontSize: 14, fontWeight: 700, flexShrink: 0 },
    lookupName: { fontSize: 13.5, fontWeight: 700, color: INK, lineHeight: 1.3 },
    lookupMeta: {
        fontSize: 11.5, color: "#5f6876", marginTop: 2,
        display: "flex", flexWrap: "wrap", gap: "2px 12px", alignItems: "center",
    },
    lookupMsg: { fontSize: 12.5, lineHeight: 1.5 },

    // ---- summary ------------------------------------------------------------
    summary: {
        display: "flex", alignItems: "center", gap: 12,
        marginTop: 22, padding: "13px 16px", borderRadius: 14,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}55`,
        fontSize: 13, color: "#7a3d12", lineHeight: 1.55,
    },
    summaryStrong: { fontWeight: 800, color: "#a8450f" },

    hintBar: {
        display: "flex", alignItems: "flex-start", gap: 9,
        padding: "8px 12px", marginBottom: 16, borderRadius: 12,
        background: "#eef3fc", border: "1px solid #cfdcf3",
        fontSize: 12, color: "#24487f", lineHeight: 1.55,
    },

    actions: {
        display: "flex", alignItems: "center", gap: 10, marginTop: 22,
        paddingTop: 16, borderTop: "1px solid #f1eeec",
        [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
    },
    btnPrimary: {
        borderRadius: 24, textTransform: "none", fontWeight: 700, padding: "8px 26px",
        background: ORANGE_DARK, color: "#fff", boxShadow: "0 6px 16px rgba(93,55,28,0.22)",
        "&:hover": { background: "#a8450f", color: "#fff", boxShadow: "0 6px 16px rgba(93,55,28,0.22)" },
        "&.Mui-disabled": { background: "#e6e2df", color: "#857d77", boxShadow: "none" },
        [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    btnGhost: {
        borderRadius: 24, textTransform: "none", fontWeight: 600, padding: "8px 20px",
        color: "#6b6460", background: "#f3f1ef", boxShadow: "none",
        "&:hover": { background: "#eae6e3", color: "#6b6460", boxShadow: "none" },
    },
    monoInput: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: 0.6 },
}));

// One hook for both lead fields: same debounce, same race guard, same shaped
// result. The original had this inline for the referrer only, and looked the
// other lead up not at all - so a typo in the new lead number was only caught
// by the server, after the gift had been attempted.
const useLeadLookup = (leadNo) => {
    const [state, setState] = useState({ status: "idle", data: null });
    // Monotonic token. Without it a slow response for the number you just
    // finished deleting can land after the fast one for what you typed next,
    // and overwrite it with stale details.
    const reqId = useRef(0);

    useEffect(() => {
        if (!LEAD_REGEX.test(leadNo)) {
            reqId.current += 1;              // cancel anything in flight
            setState({ status: "idle", data: null });
            return undefined;
        }
        const myId = reqId.current + 1;
        reqId.current = myId;
        setState({ status: "loading", data: null });

        const timer = setTimeout(async () => {
            try {
                const res = await HttpService.getByUniqueLeadNo(leadNo);
                if (reqId.current !== myId) return;
                const data = res?.data?.data || {};
                setState({ status: "found", data });
            } catch (err) {
                if (reqId.current !== myId) return;
                // 404 and a server error are different problems with different
                // fixes - telling someone "enter the email manually" when the
                // API is down sends them down the wrong path entirely.
                const notFound = err?.response?.status === 404;
                setState({ status: notFound ? "notfound" : "error", data: null });
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [leadNo]);

    return state;
};

const XoxodayForm = (props) => {
    const classes = useStyles();

    const [referredBy, setReferredBy] = useState("");
    const [referredTo, setReferredTo] = useState("");
    const [referredByEmail, setReferredByEmail] = useState("");
    // Once the email box has been typed in, a later lookup must not overwrite
    // it - that is the person correcting the system, not the other way round.
    const [emailTouched, setEmailTouched] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const byLookup = useLeadLookup(referredBy);
    const toLookup = useLeadLookup(referredTo);

    // Auto-fill from the referrer lookup, but never over a manual edit.
    useEffect(() => {
        if (emailTouched) return;
        if (byLookup.status === "found" && byLookup.data?.email) {
            setReferredByEmail(String(byLookup.data.email).trim());
        } else if (byLookup.status !== "loading") {
            setReferredByEmail("");
        }
    }, [byLookup.status, byLookup.data, emailTouched]);

    const setLead = (setter, key) => (e) => {
        setter(sanitiseLead(e.target.value));
        // Clearing as you type, rather than only on the next submit - an error
        // that stays red after you have fixed it trains people to ignore it.
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
        if (key === "referredBy") setEmailTouched(false);
    };

    const sameLead = Boolean(referredBy) && referredBy === referredTo;

    const validate = useCallback(() => {
        const next = {};
        if (!LEAD_REGEX.test(referredBy)) next.referredBy = LEAD_HINT;
        if (!LEAD_REGEX.test(referredTo)) next.referredTo = LEAD_HINT;
        if (sameLead) next.referredTo = "A lead cannot refer itself";
        if (!EMAIL_REGEX.test(referredByEmail)) next.referredByEmail = "Enter a valid email";
        return next;
    }, [referredBy, referredTo, referredByEmail, sameLead]);

    // Both leads have to exist, because the server checks the same thing and
    // answers "One or both leads not found" - better to know before pressing it.
    const isValid =
        LEAD_REGEX.test(referredBy) &&
        LEAD_REGEX.test(referredTo) &&
        EMAIL_REGEX.test(referredByEmail) &&
        !sameLead &&
        byLookup.status === "found" &&
        toLookup.status === "found";

    const looking = byLookup.status === "loading" || toLookup.status === "loading";

    const reset = () => {
        setReferredBy("");
        setReferredTo("");
        setReferredByEmail("");
        setEmailTouched(false);
        setErrors({});                 // the old Reset left stale errors on screen
    };

    const handleSubmit = async () => {
        const next = validate();
        setErrors(next);
        if (Object.keys(next).length > 0) return;

        setSubmitting(true);
        try {
            const response = await HttpService.createXoxoday({ referredBy, referredTo, referredByEmail });
            props.setSnackInfo(
                response?.data?.message || "Referral gift created successfully",
                "success"
            );
            reset();
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || "Something went wrong", "error");
        }
        setSubmitting(false);
    };

    // ---- one lookup panel, used for both leads ------------------------------
    const renderLookup = (lookup, leadNo, { emptyText }) => {
        if (!leadNo) {
            return (
                <div className={`${classes.lookupCard} ${classes.lookupIdle}`}>
                    <InfoOutlinedIcon style={{ fontSize: 18, flexShrink: 0 }} />
                    <span className={classes.lookupMsg}>{emptyText}</span>
                </div>
            );
        }
        if (!LEAD_REGEX.test(leadNo)) {
            return (
                <div className={`${classes.lookupCard} ${classes.lookupIdle}`}>
                    <InfoOutlinedIcon style={{ fontSize: 18, flexShrink: 0 }} />
                    <span className={classes.lookupMsg}>{LEAD_HINT}</span>
                </div>
            );
        }
        if (lookup.status === "loading") {
            return (
                <div className={`${classes.lookupCard} ${classes.lookupIdle}`}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <span className={classes.lookupMsg}>Looking up <b>{leadNo}</b>…</span>
                </div>
            );
        }
        if (lookup.status === "notfound") {
            return (
                <div className={`${classes.lookupCard} ${classes.lookupBad}`}>
                    <ErrorOutlineIcon style={{ fontSize: 18, color: RED, flexShrink: 0 }} />
                    <span className={classes.lookupMsg} style={{ color: RED }}>
                        No lead found with the number <b>{leadNo}</b>. Check it and try again.
                    </span>
                </div>
            );
        }
        if (lookup.status === "error") {
            return (
                <div className={`${classes.lookupCard} ${classes.lookupWarn}`}>
                    <WarningRoundedIcon style={{ fontSize: 18, color: "#7a4b00", flexShrink: 0 }} />
                    <span className={classes.lookupMsg} style={{ color: "#7a4b00" }}>
                        Could not reach the lead service. The number has not been checked.
                    </span>
                </div>
            );
        }
        const d = lookup.data || {};
        return (
            <div className={`${classes.lookupCard} ${classes.lookupOk}`}>
                <Avatar className={classes.lookupAvatar} style={{ background: "#fff", color: GREEN_DARK }}>
                    {String(d.name || "?").trim().charAt(0).toUpperCase()}
                </Avatar>
                <span style={{ minWidth: 0, flex: 1 }}>
                    <div className={classes.lookupName}>{d.name || "Unnamed customer"}</div>
                    <div className={classes.lookupMeta}>
                        {d.email
                            ? <span><MailOutlineIcon style={{ fontSize: 12, verticalAlign: "middle", marginRight: 4 }} />{d.email}</span>
                            : <span style={{ color: "#7a4b00", fontWeight: 700 }}>no email on file</span>}
                        {d.contact_no && (
                            <span><PhoneIcon style={{ fontSize: 12, verticalAlign: "middle", marginRight: 4 }} />{d.contact_no}</span>
                        )}
                    </div>
                </span>
                <Chip
                    size="small"
                    icon={<CheckCircleIcon style={{ fontSize: 14, color: GREEN_DARK }} />}
                    label="Found"
                    style={{ background: "#fff", color: GREEN_DARK, fontWeight: 700, height: 24, flexShrink: 0 }}
                />
            </div>
        );
    };

    // The live status lives in the field itself as well as the card below it,
    // so the state is visible without looking away from what you are typing.
    const leadFieldProps = (value, lookup, onClear) => ({
        InputProps: {
            classes: { input: classes.monoInput },
            endAdornment: (
                <InputAdornment position="end">
                    {lookup.status === "loading" && (
                        <CircularProgress size={16} thickness={5} style={{ color: ORANGE_DARK }} />
                    )}
                    {lookup.status === "found" && (
                        <CheckCircleIcon style={{ fontSize: 18, color: GREEN }} />
                    )}
                    {lookup.status === "notfound" && (
                        <ErrorOutlineIcon style={{ fontSize: 18, color: RED }} />
                    )}
                    {Boolean(value) && (
                        <Tooltip title="Clear">
                            <IconButton size="small" onClick={onClear} aria-label="clear">
                                <ClearIcon style={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </InputAdornment>
            ),
        },
    });

    return (
        <div className={classes.page}>
            <div className={classes.shell}>
                <div className={classes.banner}>
                    <div className={classes.bannerIcon}><CardGiftcardIcon style={{ fontSize: 24 }} /></div>
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.bannerTitle}>Referral Gift</div>
                        <div className={classes.bannerSub}>
                            Reward a customer for bringing in a new lead.
                        </div>
                    </div>
                </div>

                <div className={classes.card}>
                    {/* A reserved slot would be overkill on a form this short, but
                        the bar still has to say something is happening. */}
                    {(looking || submitting) && <BrandLinearProgress style={{ marginBottom: 14 }} />}

                    <div className={classes.hintBar}>
                        <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                        <span>
                            Both boxes take a <b>Unique Lead Number</b> — the <code>BN1234567</code> style
                            code, not a project number or a customer name. The gift goes to the{" "}
                            <b>referrer</b>; the second number is the <b>new lead they brought in</b>.
                            Both are looked up as you type.
                        </span>
                    </div>

                    {/* ---------------------------- step 1 ---------------------------- */}
                    <div className={classes.stepLabel}>
                        <span className={classes.stepNum}>1</span>
                        <PersonIcon style={{ fontSize: 18, color: ORANGE_DARK }} />
                        <span className={classes.stepTitle}>Referrer</span>
                        <span className={classes.stepSub}>— the customer who gets the gift</span>
                        <Tooltip title={LEAD_HINT}>
                            <HelpOutlineIcon style={{ fontSize: 15, color: "#9aa5b1", cursor: "help" }} />
                        </Tooltip>
                    </div>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Referrer unique lead number"
                                placeholder="BN1234567"
                                value={referredBy}
                                onChange={setLead(setReferredBy, "referredBy")}
                                error={Boolean(errors.referredBy) || byLookup.status === "notfound"}
                                // Always says what the box takes, not only
                                // once you have already got it wrong.
                                helperText={errors.referredBy || leadHelp(referredBy)}
                                inputProps={{ maxLength: 9, spellCheck: false, autoComplete: "off" }}
                                {...leadFieldProps(referredBy, byLookup, () => { setReferredBy(""); setEmailTouched(false); })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Referrer email"
                                placeholder="name@example.com"
                                value={referredByEmail}
                                onChange={(e) => { setEmailTouched(true); setReferredByEmail(e.target.value); setErrors((p) => (p.referredByEmail ? { ...p, referredByEmail: undefined } : p)); }}
                                error={Boolean(errors.referredByEmail)}
                                helperText={errors.referredByEmail
                                    || (emailTouched ? "Edited manually" : "Filled in from the lead — you can change it")}
                                InputProps={{
                                    endAdornment: referredByEmail ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Clear the email">
                                                <IconButton size="small" onClick={() => { setEmailTouched(true); setReferredByEmail(""); }} aria-label="clear email">
                                                    <ClearIcon style={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>
                    </Grid>

                    {renderLookup(byLookup, referredBy, { emptyText: "Enter the referrer's unique lead number to pull up their details." })}

                    <div className={classes.divider}>
                        <span className={classes.dividerLine} />
                        <span className={classes.dividerIcon}><ArrowDownwardIcon style={{ fontSize: 17 }} /></span>
                        <span className={classes.dividerLine} />
                    </div>

                    {/* ---------------------------- step 2 ---------------------------- */}
                    <div className={classes.stepLabel}>
                        <span className={classes.stepNum}>2</span>
                        <PersonAddIcon style={{ fontSize: 18, color: ORANGE_DARK }} />
                        <span className={classes.stepTitle}>New referred lead</span>
                        <span className={classes.stepSub}>— who they brought in</span>
                        <Tooltip title={LEAD_HINT}>
                            <HelpOutlineIcon style={{ fontSize: 15, color: "#9aa5b1", cursor: "help" }} />
                        </Tooltip>
                    </div>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="New unique lead number"
                                placeholder="BN7654321"
                                value={referredTo}
                                onChange={setLead(setReferredTo, "referredTo")}
                                onKeyDown={(e) => { if (e.key === "Enter" && isValid && !submitting) handleSubmit(); }}
                                error={Boolean(errors.referredTo) || toLookup.status === "notfound" || sameLead}
                                helperText={errors.referredTo || (sameLead ? "A lead cannot refer itself" : leadHelp(referredTo))}
                                inputProps={{ maxLength: 9, spellCheck: false, autoComplete: "off" }}
                                {...leadFieldProps(referredTo, toLookup, () => setReferredTo(""))}
                            />
                        </Grid>
                    </Grid>

                    {renderLookup(toLookup, referredTo, { emptyText: "Enter the new unique lead number to confirm who it is." })}

                    {/* Plain-English restatement of what is about to happen. Two
                        seven-digit codes look alike; two names do not. */}
                    {isValid && (
                        <div className={classes.summary}>
                            <CardGiftcardIcon style={{ fontSize: 20, color: ORANGE_DARK, flexShrink: 0 }} />
                            <span>
                                <span className={classes.summaryStrong}>{byLookup.data?.name || referredBy}</span>
                                {" "}will receive the gift at{" "}
                                <span className={classes.summaryStrong}>{referredByEmail}</span>
                                {" "}for referring{" "}
                                <span className={classes.summaryStrong}>{toLookup.data?.name || referredTo}</span>.
                            </span>
                        </div>
                    )}

                    <div className={classes.actions}>
                        <Button
                            className={classes.btnGhost}
                            onClick={reset}
                            disabled={submitting}
                            startIcon={<RefreshIcon style={{ fontSize: 17 }} />}
                        >
                            Reset
                        </Button>
                        <Tooltip title={isValid ? "" : "Both lead numbers must be found, and a valid referrer email is required"}>
                            <span style={{ marginLeft: "auto" }}>
                                <Button
                                    className={classes.btnPrimary}
                                    onClick={handleSubmit}
                                    disabled={!isValid || submitting || looking}
                                    startIcon={submitting
                                        ? <CircularProgress size={16} thickness={5} style={{ color: "#fff" }} />
                                        : <SendIcon style={{ fontSize: 17 }} />}
                                >
                                    {submitting ? "Creating…" : "Create Gift"}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>

                <Typography style={{ fontSize: 11.5, color: "#5f6876", textAlign: "center", marginTop: 12 }}>
                    Gift vouchers are issued through Xoxoday once the referral is created.
                </Typography>
            </div>
        </div>
    );
};

export default connect(null, { setSnackInfo })(XoxodayForm);